import { forwardRef, useRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import { isDevEnvironment } from '../../utils/environment';

const STATIC_CDN_DEV = 'https://static.dev.agendario.com.br';
const STATIC_CDN_PROD = 'https://static.agendario.com.br';
const CDN_BASE = isDevEnvironment() ? STATIC_CDN_DEV : STATIC_CDN_PROD;

// Converte URL relativa do template para URL absoluta no CDN estático.
// O backend retorna caminhos relativos (ex: /pages/templates/prof-clean.html).
function resolveTemplateUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return CDN_BASE + url;
}

/**
 * SitePreview — Preview do site via iframe + postMessage (§9.4 do plano).
 *
 * Carrega o HTML do template em modo preview (?preview=1) e mantém sincronia
 * com o editor via postMessage. Protocolo:
 *   { type: 'AGENDARIO_PREVIEW', action: 'FULL_REFRESH' | 'PATCH', payload: {...} }
 *
 * Expõe via ref:
 *   - refresh()       → envia FULL_REFRESH com config atual (botão Preview)
 *   - sendPatch(patch) → envia PATCH parcial (mudanças automáticas §9.2, debounce no chamador)
 *
 * @param {object} template - { id, name, htmlUrl, supported_modes, ... }
 * @param {object} config - SiteConfigDto atual (§6.2)
 * @param {string} siteType - tipo de site ('link_in_bio'|'professional_site'|'company_site')
 * @param {React.Ref} ref - ref expondo refresh() e sendPatch()
 *
 * Obrigação: P15 — NÃO dispara refresh em mudanças de texto livre.
 * Refresh automático ocorre APENAS para campos §9.2 (select/color/etc).
 */
const SitePreview = forwardRef(function SitePreview(
  { template, config, siteType = 'link_in_bio' },
  ref
) {
  const iframeRef = useRef(null);
  const configRef = useRef(config);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  function sendMessage(action, payload) {
    if (!iframeRef.current?.contentWindow) return;
    try {
      iframeRef.current.contentWindow.postMessage(
        { type: 'AGENDARIO_PREVIEW', action, payload },
        '*'
      );
    } catch (err) {
      console.error('[SitePreview] postMessage error:', err);
    }
  }

  useImperativeHandle(ref, () => ({
    refresh() {
      sendMessage('FULL_REFRESH', { config: configRef.current });
    },
    sendPatch(patch) {
      sendMessage('PATCH', { patch });
    },
  }));

  function handleLoad() {
    setIsLoading(false);
    setError(null);
    // Pequeno delay para garantir que o iframe está pronto
    setTimeout(() => {
      sendMessage('FULL_REFRESH', { config: configRef.current });
    }, 100);
  }

  function handleError() {
    setIsLoading(false);
    setError('Erro ao carregar o preview do template.');
  }

  // Listener para mensagens do iframe (debug/errors)
  useEffect(() => {
    function handleMessage(e) {
      if (e.data?.type === 'AGENDARIO_PREVIEW_ERROR') {
        console.error('[SitePreview] iframe error:', e.data.message);
      } else if (e.data?.type === 'AGENDARIO_PREVIEW_LOG' && process.env.NODE_ENV === 'development') {
        console.log('[SitePreview iframe]', e.data.message);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const templateUrl = resolveTemplateUrl(template?.htmlUrl);
  const iframeUrl = templateUrl ? `${templateUrl}?preview=1` : null;

  if (!iframeUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
          color: 'text.secondary',
          gap: 2,
        }}
      >
        <PreviewIcon sx={{ fontSize: 56, opacity: 0.25 }} />
        <Typography variant="body2" textAlign="center">
          Selecione um template para ver o preview em tempo real.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'error.light',
            p: 2,
            zIndex: 2,
          }}
        >
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <Box
        component="iframe"
        ref={iframeRef}
        src={iframeUrl}
        onLoad={handleLoad}
        onError={handleError}
        title={`Preview: ${template?.name || 'Template'}`}
        sandbox="allow-scripts allow-same-origin"
        sx={{ width: '100%', height: '100%', border: 'none', display: 'block', minHeight: 400 }}
      />
    </Box>
  );
});

SitePreview.displayName = 'SitePreview';

export default SitePreview;
